import { gx } from "@/components/gtax/base";
import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";

export const NAV = [
  { to: gx("/about"), label: "Who We Are", id: "about" },
  { to: gx("/strategy"), label: "Our Strategy", id: "strategy" },
  { to: gx("/report"), label: "Performance", id: "report" },
  { to: gx("/membership"), label: "Membership", id: "membership" },
  { to: gx("/contact"), label: "Contact", id: "contact" },
];

const LEGAL_NAV = [
  { to: gx("/disclaimer"), label: "Disclaimer" },
  { to: gx("/risk"), label: "Risk Disclosure" },
  { to: gx("/terms"), label: "Membership Agreement" },
  { to: gx("/privacy"), label: "Privacy Policy" },
];

const Brand = () => (
  <Link to={gx()} className="flex items-center gap-4" data-testid="gtax-nav-logo">
    <img src="/gtax/logo.png" alt="GTA X" className="w-14 h-14 md:w-20 md:h-20 object-contain shrink-0" />
    <span className="leading-none whitespace-nowrap">
      <span className="gx-serif block text-3xl md:text-4xl tracking-wide">GTA X</span>
      <span className="block text-[11px] md:text-[12px] tracking-[.28em] uppercase text-[color:var(--gold)] mt-1">Gold Trading Association</span>
      <span className="hidden md:block text-[11px] tracking-[.2em] uppercase text-[color:var(--mute)] mt-1">A product of DFI Holdings</span>
    </span>
  </Link>
);

export const Nav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl gx-nav border-b border-[color:var(--line)]" data-testid="gtax-nav">
      <div className="gx-wrap h-28 flex items-center justify-between gap-6">
        <Brand />
        <nav className="hidden xl:flex gap-7">
          {NAV.map((n) => (
            <NavLink key={n.id} to={n.to} className="gx-navlink" data-testid={`gtax-nav-${n.id}`}>{n.label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block"><Link to={gx("/access")} className="gx-btn !py-3 !px-6" data-testid="gtax-nav-cta">Request access</Link></div>
          <button className="xl:hidden p-2 -mr-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="gtax-nav-menu-toggle">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t border-[color:var(--line)] bg-[color:var(--bg)]" data-testid="gtax-mobile-menu">
          <div className="gx-wrap py-6 flex flex-col gap-5">
            {[...NAV, { to: gx("/access"), label: "Request Access", id: "access" }].map((n) => (
              <NavLink key={n.id} to={n.to} onClick={() => setOpen(false)} className="gx-navlink" data-testid={`gtax-mobile-nav-${n.id}`}>{n.label}</NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <>
  <section className="gx-line py-10 bg-[color:var(--ink)] text-[#EFE4CF]" data-testid="gtax-risk-bar">
    <div className="gx-wrap grid md:grid-cols-12 gap-8 text-sm leading-relaxed">
      <div className="md:col-span-3">
        <div className="text-xs uppercase tracking-[.22em] font-semibold text-[#D9BE7E]">Status &amp; risk warning</div>
      </div>
      <p className="md:col-span-5 opacity-90">
        GTA X is a private, invitation-only programme of DFI Holdings. It is not a public offer, not a regulated collective investment
        fund, and has not been approved by any financial regulator. Participation may be restricted in your jurisdiction.
        Trades are executed through VT Markets Limited (FSC Mauritius, licence GB23202269). The broker's licence covers the broker, not GTA X.
      </p>
      <p className="md:col-span-4 opacity-90">
        No return is guaranteed. Any profit share is paid only from realised gains.
        Past performance doesn't guarantee future results.
      </p>
    </div>
  </section>
  <footer className="pt-20 pb-12 gx-line text-[color:var(--mute)]" data-testid="gtax-footer">
    <div className="gx-wrap grid md:grid-cols-12 gap-12">
      <div className="md:col-span-4">
        <div className="flex items-center gap-4">
          <img src="/gtax/logo.png" alt="" className="w-16 h-16 object-contain" />
          <div>
            <div className="gx-serif text-3xl text-[color:var(--ink)]">GTA X</div>
            <div className="text-xs tracking-[.24em] uppercase text-[color:var(--gold)]">Gold Trading Association</div>
          </div>
        </div>
        <p className="mt-6 text-sm">A product of DFI Holdings · Abu Dhabi, United Arab Emirates</p>
        <a href="mailto:support@gta-x.io" className="mt-2 inline-block text-sm hover:text-[color:var(--gold2)]" data-testid="gtax-footer-email">support@gta-x.io</a>
      </div>
      <div className="md:col-span-2">
        <div className="gx-eyebrow mb-5">Association</div>
        <ul className="space-y-3 text-sm">
          {NAV.map((n) => <li key={n.id}><Link to={n.to} className="hover:text-[color:var(--gold2)] transition-colors" data-testid={`gtax-footer-${n.id}`}>{n.label}</Link></li>)}
          <li><Link to={gx("/access")} className="hover:text-[color:var(--gold2)] transition-colors" data-testid="gtax-footer-access">Request Access</Link></li>
          <li><Link to={gx("/status")} className="hover:text-[color:var(--gold2)] transition-colors" data-testid="gtax-footer-status">Member Status</Link></li>
          <li><Link to={gx("/app")} className="hover:text-[color:var(--gold2)] transition-colors" data-testid="gtax-footer-app">Get the app</Link></li>
        </ul>
      </div>
      <div className="md:col-span-2">
        <div className="gx-eyebrow mb-5">Legal</div>
        <ul className="space-y-3 text-sm">
          {LEGAL_NAV.map((n) => <li key={n.to}><Link to={n.to} className="hover:text-[color:var(--gold2)] transition-colors" data-testid={`gtax-footer-legal-${n.to.split("/").pop()}`}>{n.label}</Link></li>)}
        </ul>
      </div>
      <p className="md:col-span-4 text-xs leading-relaxed" data-testid="gtax-footer-disclaimer">
        Important: GTA X does not offer or guarantee any fixed or periodic return. Member distributions are paid only from realised
        net trading profit. Leveraged trading in gold carries a high level of risk.
        Past performance doesn't guarantee future results.
        Invitation-only; not a public offer. <Link to={gx("/disclaimer")} className="underline">Read the full disclaimer</Link>.
      </p>
    </div>
    <div className="gx-wrap mt-14 pt-8 gx-line text-xs flex flex-wrap justify-between gap-4">
      <span>© 2026 DFI Holdings. All rights reserved.</span>
      <span>GTA X · Consolidated Gold Mandate · XAU/USD</span>
    </div>
  </footer>
  </>
);

export const PageHead = ({ eyebrow, title, sub, img }) => (
  <section className="pt-44 pb-20 relative overflow-hidden" data-testid="gtax-page-head">
    {img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[.22] pointer-events-none" />}
    <div className="gx-wrap relative">
      <p className="gx-eyebrow">{eyebrow}</p>
      <h1 className="gx-serif text-4xl sm:text-5xl lg:text-6xl mt-6 max-w-4xl leading-[1.05]">{title}</h1>
      {sub && <p className="mt-8 max-w-2xl text-lg text-[color:var(--mute)] leading-relaxed">{sub}</p>}
    </div>
  </section>
);
